import React from "react";
import { useTranslation } from "react-i18next";

interface ArtefactResizeHandleProps {
  isResizing: boolean;
  onMouseDown: (e: React.MouseEvent) => void;
  onKeyboardResize: (delta: number) => void;
  onDoubleClick: () => void;
  /** Current height in px */
  valueNow: number;
  /** Minimum allowed height in px */
  valueMin: number;
  /** Maximum allowed height in px */
  valueMax: number;
}

const KEYBOARD_STEP = 20; // pixels per keypress

/**
 * A horizontal draggable resize handle for adjusting the height of an
 * expanded artefact card. Sits at the bottom of the card body — drag DOWN
 * to grow the card, UP to shrink. Mirrors the visual style of
 * PromptResizeHandle.
 *
 * Keyboard accessible: ArrowDown grows, ArrowUp shrinks.
 * Double-click resets to the default height.
 */
export const ArtefactResizeHandle: React.FC<ArtefactResizeHandleProps> = ({
  isResizing,
  onMouseDown,
  onKeyboardResize,
  onDoubleClick,
  valueNow,
  valueMin,
  valueMax,
}) => {
  const { t } = useTranslation();

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      // Drag direction parity: ArrowDown grows (positive), ArrowUp shrinks
      const delta = e.key === "ArrowDown" ? KEYBOARD_STEP : -KEYBOARD_STEP;

      onKeyboardResize(delta);
    }
  };

  return (
    <div
      aria-label={t("Resize artefact")}
      aria-orientation="horizontal"
      aria-valuemax={Math.round(valueMax)}
      aria-valuemin={Math.round(valueMin)}
      aria-valuenow={Math.round(valueNow)}
      className="relative cursor-row-resize flex items-center justify-center my-1 focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:outline-none"
      data-testid="artefact-resize-handle"
      role="separator"
      tabIndex={0}
      onDoubleClick={onDoubleClick}
      onKeyDown={handleKeyDown}
      onMouseDown={onMouseDown}
    >
      {/* Thin horizontal line — matches the divider style */}
      <div className={`h-px w-full bg-divider ${isResizing ? "bg-default-300" : ""}`} />
      {/* Grab handle — centred pill with grip lines */}
      <div
        className={`absolute flex flex-col gap-0.5 py-1.5 px-2 rounded transition-colors duration-150 ease-out ${isResizing ? "bg-default-200" : "bg-default-100 hover:bg-default-200"}`}
      >
        <div className="h-px w-1 bg-default-400" />
        <div className="h-px w-1 bg-default-400" />
        <div className="h-px w-1 bg-default-400" />
      </div>
    </div>
  );
};
